import {BaseComponent} from './base-component.js'
import {customElement, property} from 'lit/decorators.js'
import { html, css, CSSResultGroup, TemplateResult, svg } from 'lit'

export interface RadioOption {
  id: string
  label: string
  hint?: string
  value: string | number | object
  name: string
  checked: boolean
  disabled: boolean
  extraContent?: string
}

@customElement('il-radio-group')
export default class RadioGroupComponent extends BaseComponent {
  @property({type: Array}) options: RadioOption[] = []
  @property({type: Object}) selected?: RadioOption
  @property({type: String}) label?: string | number;
  @property({type: String}) hint?: string | number;
  @property({type: String, attribute: 'error-message'}) errorMessage?: string;
  @property({type: Function}) onClick?: (option: RadioOption) => void

  static override styles = [
    BaseComponent.styles,
    css`
      .il-radio-group-container {
        display: flex;
        flex-direction: column;
      }

      .il-radio-group-container .il-radio-group__hint {
        color: var(--il-color-neutrals-300);
        display: inline-block;
        font-size: var(--il-font-size-12);
        line-height: var(--il-font-line-height-150);
        letter-spacing: var(--il-font-letter-spacing-1);
      }

      .il-radio-group-container .il-radio-group__error-message {
        display: flex;
        align-items: flex-start;
        gap: var(--il-size-8);

        color: var(--il-color-warning);
        font-size: var(--il-font-size-14);
        line-height: var(--il-font-line-height-140);
      }
    `,
  ] as CSSResultGroup

  override render () {
    return html`
      <div
        class="${this.baseCSSClass} il-radio-group-container"
        data-test-iliad-radio-group
      >
        <div class="il-radio-group__label-container">
          <slot name="label-before" data-test-iliad-radio-group-label-before></slot>
          ${this.renderLabel()}
          <slot name="label-after" data-test-iliad-radio-group-label-after></slot>
        </div>

        <div class="il-radio-group__hint">
          <slot name="hint" data-test-iliad-radio-group-hint>
            ${this.hint}
          </slot>
        </div>

        ${this.options.map((option: RadioOption) => {
          return html`
            <il-radio
              id="${option.id}"
              label="${option.label}"
              hint="${option.hint}"
              value="${option.value}"
              name="${option.name}"
              truncate-hint="true"
              extra-content="${option.extraContent}"
              selected="${option?.id === this.preSelectedOption?.id ||
              option?.id === this.selected?.id}"
              .onClick="${this._onClick.bind(this, option)}"
              ?checked=${option.checked}
              ?disabled=${option.disabled}
              data-test-iliad-radio-group-option="${option.id || option.value}"
            >
            </il-radio>
          `
        })}

        ${this.renderErrorMessage()}
      </div>
    `
  }

  private renderLabel(): TemplateResult {
    if (this.label) {
      return html`<label
        for=${this.internalId}
        class="il-radio-group__label"
        data-test-iliad-radio-group-label
      >
        <slot name="label">${this.label}</slot>
      </label>`;
    }

    return html``;
  }

  /**
   * returns an internal id to be used on the input and label
   * gets the @id property passed from the glimmer component and adds so it is a unique ID
   *
   * if this component is used without glimmer, this should also work by passing the @id property
   * when calling the web component HTML tag, e.g.:
   * <iliad-input id="some-id"></iliad-input>
   */
  get internalId(): string {
    return `iliad-${this.id}`;
  }

  private renderErrorMessage(): TemplateResult {
    if (this.errorMessage) {
      return html`<div
        class="il-radio-group__error-message"
        data-test-iliad-radio-group-error-message
      >
        ${this.alertIcon}
        ${this.errorMessage}
      </div>`;
    }

    return html``;
  }
  
  get preSelectedOption (): RadioOption | undefined {
    return this.options.find((option: RadioOption) => option.checked)
  }

  private _onClick (option: RadioOption): void {
    this.selected = option

    this.onClick?.(option)
  }

  override updated (changedProperties: Map<string, unknown>) {
    if (this.preSelectedOption) {
      this.preSelectedOption.checked = false
    }

    const selected = changedProperties.has('selected')

    if (selected) {
      const previousElement = changedProperties.get('selected') as RadioOption
      const previousElementId = previousElement?.id
      const currentElementId = this.selected?.id

      // only uncheck the previous element if it's different from the current selection
      if (previousElementId && previousElementId !== currentElementId) {
        const previousOption = this.shadowRoot
          ?.getElementById(previousElementId)
          ?.shadowRoot?.querySelector('input')

        if (previousOption) {
          previousOption.checked = false
        }
      }
    } else {
      return
    }
  }

  private alertIcon = svg`<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
  <g id="Icon20/Alert">
  <path id="Vector" d="M9.12499 10.792H10.875V5.85399H9.12499V10.792ZM9.99999 14.188C10.236 14.188 10.441 14.108 10.615 13.948C10.7883 13.788 10.875 13.576 10.875 13.312C10.875 13.076 10.7883 12.8713 10.615 12.698C10.441 12.5247 10.236 12.438 9.99999 12.438C9.76399 12.438 9.55899 12.5247 9.38499 12.698C9.21166 12.8713 9.12499 13.076 9.12499 13.312C9.12499 13.576 9.21166 13.788 9.38499 13.948C9.55899 14.108 9.76399 14.188 9.99999 14.188ZM9.99999 18.333C8.84733 18.333 7.76399 18.1143 6.74999 17.677C5.73599 17.2397 4.85399 16.646 4.10399 15.896C3.35399 15.146 2.76033 14.264 2.32299 13.25C1.88566 12.236 1.66699 11.1527 1.66699 9.99999C1.66699 8.84733 1.88566 7.76399 2.32299 6.74999C2.76033 5.73599 3.35399 4.85399 4.10399 4.10399C4.85399 3.35399 5.73599 2.76033 6.74999 2.32299C7.76399 1.88566 8.84733 1.66699 9.99999 1.66699C11.1527 1.66699 12.236 1.88566 13.25 2.32299C14.264 2.76033 15.146 3.35399 15.896 4.10399C16.646 4.85399 17.2397 5.73599 17.677 6.74999C18.1143 7.76399 18.333 8.84733 18.333 9.99999C18.333 11.1527 18.1143 12.236 17.677 13.25C17.2397 14.264 16.646 15.146 15.896 15.896C15.146 16.646 14.264 17.2397 13.25 17.677C12.236 18.1143 11.1527 18.333 9.99999 18.333ZM9.99999 16.583C11.8193 16.583 13.3713 15.9407 14.656 14.656C15.9407 13.3713 16.583 11.8193 16.583 9.99999C16.583 8.18066 15.9407 6.62866 14.656 5.34399C13.3713 4.05933 11.8193 3.41699 9.99999 3.41699C8.18066 3.41699 6.62866 4.05933 5.34399 5.34399C4.05933 6.62866 3.41699 8.18066 3.41699 9.99999C3.41699 11.8193 4.05933 13.3713 5.34399 14.656C6.62866 15.9407 8.18066 16.583 9.99999 16.583Z" fill="currentColor"/>
  </g>
  </svg>
  `;
}
